import store from '@/store';
import { CurrentLang } from 'utils/lang';
import errorMonitoring from '../../errorMonitoring';

/**
 * ReserhubApi function that creates an API client for making requests to the reserhub API
 * @returns {Object} An object with methods for interacting with the reserhub API
 */
const ReserhubApi = () => {
  let baseUrl = null;

  const validateApiConfig = () => {
    if (!baseUrl) {
      errorMonitoring.notify({
        error: new Error('Reserhub API is not initialized'),
        info: { from: 'reserhubApi validateApiConfig' },
      });
      throw new Error('Reserhub API is not initialized');
    }
  };

  /**
   * Initialize the API client with configuration from the store
   */
  const initialize = () => {
    const state = store.getState();
    const reserhub = state.whitelabelConfig?.env?.reserhub;
    baseUrl = reserhub?.api?.baseUrl || null;
  };

  /**
   * Make a request to the API
   * @param {string} endpoint - The endpoint to request
   * @param {Object} options - Request options
   * @returns {Promise<any>} The response data
   */
  const request = async (endpoint, options = {}) => {
    if (!baseUrl) {
      initialize();
    }

    const url = `${baseUrl}${endpoint}`;

    const defaultOptions = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const mergedOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    };

    const response = await fetch(url, mergedOptions);

    if (!response.ok) {
      const error = new Error(`API request failed: ${response.status} ${response.statusText}`);
      error.status = response.status;
      error.response = response;
      throw error;
    }

    // Handle empty responses
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return null;
  };

  /**
   * Make a GET request
   * @param {string} endpoint - The endpoint to request
   * @param {Object} options - Additional request options
   * @returns {Promise<any>} The response data
   */
  const get = (endpoint, options = {}) => {
    return request(endpoint, { ...options, method: 'GET' });
  };

  /**
   * Make a POST request
   * @param {string} endpoint - The endpoint to request
   * @param {Object} options - Additional request options
   * @returns {Promise<any>} The response data
   */
  const post = (endpoint, options = {}) => {
    return request(endpoint, { ...options, method: 'POST' });
  };

  /**
   * Get the current user
   * @returns {Promise<Object|null>} The user data or null if not authenticated
   */
  const getCurrentUser = () => {
    validateApiConfig();
    return get('/accounts/auth/me');
  };

  /**
   * Logout the current user
   * @returns {Promise<boolean>} Whether the logout was successful
   */
  const logout = () => {
    validateApiConfig();
    return get('/accounts/auth/logout');
  };

  /**
   * Get the next purchase for the current user
   * @returns {Promise<Object|null>} The next purchase data or null if not available
   */
  const getUpcomingTrips = async () => {
    validateApiConfig();
    return get('/commerce/accounts/upcoming-trips');
  };

  /**
   * Get connected accounts for the current user
   * @returns {Promise<Object>} The connected accounts response
   */
  const getConnectedAccounts = () => {
    validateApiConfig();
    return get('/accounts/users/me/connected-accounts');
  };

  /**
   * Get connected account by provider
   * @param {Object} options - Options object
   * @param {string} options.provider - The provider to get the connected account for (e.g. 'doters')
   * @returns {Promise<Object>} The connected accounts response
   */
  const getConnectedAccountByProvider = ({ provider }) => {
    validateApiConfig();
    const encodedProvider = encodeURIComponent(provider);
    return get(`/accounts/users/me/connected-accounts/${encodedProvider}`);
  };

  /**
   * Get wallets for the current user
   * @returns {Promise<Object>} The wallets response
   */
  const getWallets = () => {
    validateApiConfig();
    return get('/accounts/users/me/wallets');
  };

  /**
   * Login with Doters
   * @returns {Promise<Object>} The login response
   */
  const loginWithDoters = (language, redirectUrl) => {
    validateApiConfig();
    return post('/accounts/auth/doters/login', {
      body: JSON.stringify({
        language: language || CurrentLang(),
        redirectUrl: redirectUrl || encodeURIComponent(window.location.href),
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  return {
    initialize,
    request,
    getCurrentUser,
    logout,
    getUpcomingTrips,
    getConnectedAccounts,
    getConnectedAccountByProvider,
    getWallets,
    loginWithDoters,
  };
};

// Export a singleton instance
const reserhubApi = ReserhubApi();
export default reserhubApi;
